/**
 * OpenRailwayMap REST API adapter — browser fetch.
 *
 * The ORM API provides two endpoints:
 *  - Facility search: ?q=name or ?uic=NNNNN or ?ref=XXXXX
 *  - Milestone search: ?line=XXXXX&km=NNN
 *
 * Fair-use: small, public, non-commercial use only.
 * No bulk extraction. Requires identifiable User-Agent.
 * Respects HTTP 429 with single retry.
 *
 * See: https://wiki.openstreetmap.org/wiki/OpenRailwayMap/API
 */

const ORM_API_BASE = 'https://api.openrailwaymap.org/';
const USER_AGENT = 'era-open-railway-maps/0.1.0 (https://github.com/era-open-railway-maps)';

export interface OrmFacility {
  id: number;
  name: string;
  uic_ref?: string;
  railway?: string;
  lat: number;
  lon: number;
  tags?: Record<string, string>;
}

export interface OrmFacilityResult {
  facilities: OrmFacility[];
}

export interface OrmMilestone {
  id: number;
  line: string;
  km: number;
  lat: number;
  lon: number;
  tags?: Record<string, string>;
}

export interface OrmMilestoneResult {
  milestones: OrmMilestone[];
}

const ormFetch = async (url: string): Promise<unknown> => {
  const doRequest = () =>
    fetch(url, {
      headers: {
        'User-Agent': USER_AGENT,
        Referer: 'https://github.com/era-open-railway-maps',
        Accept: 'application/json',
      },
    });

  let response = await doRequest();
  if (response.status === 429) {
    await sleep(1000);
    response = await doRequest();
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new OrmApiError(response.status, text.slice(0, 500));
  }
  return response.json();
};

/**
 * Search ORM for facilities (operational points) by name, UIC code, or ref.
 */
export async function fetchFacility(
  searchTerm: string,
  searchType: 'name' | 'uic' | 'ref',
  limit = 10,
): Promise<OrmFacilityResult> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (searchType === 'name') params.set('q', searchTerm);
  else if (searchType === 'uic') params.set('uic_ref', searchTerm);
  else params.set('ref', searchTerm);

  const url = `${ORM_API_BASE}facility?${params.toString()}`;
  const raw = (await ormFetch(url)) as { features?: unknown[] };

  // ORM returns GeoJSON FeatureCollection or array
  if (Array.isArray(raw)) {
    return { facilities: raw.map(ormFeatureToFacility) };
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { features?: unknown[] }).features)) {
    return { facilities: (raw.features ?? []).map(ormFeatureToFacility) };
  }
  return { facilities: [] };
}

/**
 * Search ORM for milestones by line reference and approximate km.
 */
export async function fetchMilestone(
  lineRef: string,
  km: number,
): Promise<OrmMilestoneResult> {
  const params = new URLSearchParams({ line: lineRef, km: String(km) });
  const url = `${ORM_API_BASE}milestone?${params.toString()}`;
  const raw = (await ormFetch(url)) as { features?: unknown[] };

  if (Array.isArray(raw)) {
    return { milestones: raw.map(ormFeatureToMilestone) };
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { features?: unknown[] }).features)) {
    return { milestones: (raw.features ?? []).map(ormFeatureToMilestone) };
  }
  return { milestones: [] };
}

// ---------------------------------------------------------------------------
// Converters
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ormFeatureToFacility(f: any): OrmFacility {
  const props = f?.properties ?? f ?? {};
  const coords = f?.geometry?.coordinates ?? [props.lon ?? 0, props.lat ?? 0];
  return {
    id: props.id ?? props.osm_id ?? 0,
    name: props.name ?? '',
    uic_ref: props.uic_ref,
    railway: props.railway,
    lat: typeof coords[1] === 'number' ? coords[1] : props.lat ?? 0,
    lon: typeof coords[0] === 'number' ? coords[0] : props.lon ?? 0,
    tags: props.tags,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ormFeatureToMilestone(f: any): OrmMilestone {
  const props = f?.properties ?? f ?? {};
  const coords = f?.geometry?.coordinates ?? [props.lon ?? 0, props.lat ?? 0];
  return {
    id: props.id ?? props.osm_id ?? 0,
    line: props.line ?? props['railway:ref'] ?? '',
    km: parseFloat(props.km ?? '0'),
    lat: typeof coords[1] === 'number' ? coords[1] : props.lat ?? 0,
    lon: typeof coords[0] === 'number' ? coords[0] : props.lon ?? 0,
    tags: props.tags,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class OrmApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly body: string,
  ) {
    super(`ORM API returned HTTP ${statusCode}: ${body}`);
    this.name = 'OrmApiError';
  }
}
